import { useCallback } from "react"
import { useMutation } from "urql"

import "@/screens/Companies/edit/graphql/comments.query"

import type {
  CommentCreateFailure,
  CommentUpdateFailure,
  CreateCommentMutationMutation,
  CreateCommentMutationMutationVariables,
  UpdateCommentMutationMutation,
  UpdateCommentMutationMutationVariables,
} from "@/graphql/codegen/graphql"
import {
  CommentParentTableType,
  CreateCommentMutationDocument,
  UpdateCommentMutationDocument,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UpsertTransferCommentParams {
  transferId: string
  value: string
  existingCommentId?: string | null
}

export interface UseUpsertTransferCommentReturn {
  upsertComment: (params: UpsertTransferCommentParams) => Promise<void>
  isUpserting: boolean
}

export const useUpsertTransferComment = (): UseUpsertTransferCommentReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching: isCreating }, executeCreateComment] = useMutation<
    CreateCommentMutationMutation,
    CreateCommentMutationMutationVariables
  >(CreateCommentMutationDocument)

  const [{ fetching: isUpdating }, executeUpdateComment] = useMutation<
    UpdateCommentMutationMutation,
    UpdateCommentMutationMutationVariables
  >(UpdateCommentMutationDocument)

  const upsertComment = useCallback(
    async ({ transferId, value, existingCommentId }: UpsertTransferCommentParams): Promise<void> => {
      const { data, error } = existingCommentId
        ? await executeUpdateComment({ input: { commentId: existingCommentId, value } })
        : await executeCreateComment({
            input: { value, referenceId: transferId, type: CommentParentTableType.Transfer, isActive: true },
          })

      const output = data && ("update" in data.comment ? data.comment.update : data.comment.create)
      const hasError =
        error || output?.__typename === "CommentCreateFailure" || output?.__typename === "CommentUpdateFailure"

      if (hasError) {
        const message = error?.message ?? (output as CommentCreateFailure | CommentUpdateFailure).error.message
        notify({ type: NotificationType.Error, message })
      }
    },
    [executeCreateComment, executeUpdateComment, notify]
  )

  return { upsertComment, isUpserting: isCreating || isUpdating }
}
